// Encode/decode the user's search inputs into a URL query string so a search
// can be shared by link or saved under a user profile.

const CITY_SEP = "|";

const cityKey = (c) =>
  c ? [c.city || "", c.admin || "", c.country_code || ""].join(CITY_SEP) : "";

const parseCityKey = (s) => {
  if (!s || typeof s !== "string") return null;
  const [city, admin, country_code] = s.split(CITY_SEP);
  return { city: city || "", admin: admin || "", country_code: country_code || "" };
};

const toYMD = (d) => {
  if (!d) return "";
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fromYMD = (s) => {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const b64encode = (obj) => {
  try {
    const json = JSON.stringify(obj);
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  } catch {
    return "";
  }
};

const b64decode = (s) => {
  try {
    let str = (s || "").replace(/-/g, "+").replace(/_/g, "/");
    while (str.length % 4) str += "=";
    return JSON.parse(decodeURIComponent(escape(atob(str))));
  } catch {
    return null;
  }
};

// Build a flat params object from current search state. Empty values are dropped.
export function buildSearchParams(state) {
  const {
    origin,
    destination,
    departureDate,
    returnDate,
    adults,
    children,
    flightType,
    airbnbType,
    searchType,
    minReviewCount,
    minReviewScore,
    userCurrency,
    advancedFilters,
  } = state || {};

  const params = {};
  const o = cityKey(origin);
  const d = cityKey(destination);
  if (o) params.o = o;
  if (d) params.d = d;
  const dd = toYMD(departureDate);
  const rd = toYMD(returnDate);
  if (dd) params.dd = dd;
  if (rd) params.rd = rd;
  if (Number.isFinite(adults)) params.a = String(adults);
  if (Number.isFinite(children)) params.c = String(children);
  if (flightType) params.ft = flightType;
  if (airbnbType) params.at = airbnbType;
  if (searchType) params.st = searchType;
  if (Number.isFinite(minReviewCount)) params.mrc = String(minReviewCount);
  if (minReviewScore != null && minReviewScore !== "") params.mrs = String(minReviewScore);
  if (userCurrency) params.cur = userCurrency;
  if (advancedFilters && Object.keys(advancedFilters).length > 0) {
    const enc = b64encode(advancedFilters);
    if (enc) params.af = enc;
  }
  return params;
}

// Convert a params object to a URLSearchParams query string (without leading "?").
export function paramsToQueryString(params) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v != null && v !== "") sp.set(k, v);
  }
  return sp.toString();
}

// Parse a URLSearchParams / window.location.search into a partial state object.
// Origin/destination are returned as parsed { city, admin, country_code } keys —
// the caller must look up the full city object from iata_cities.json (different
// callers have different data sources).
export function parseSearchParams(searchString) {
  const sp =
    typeof searchString === "string"
      ? new URLSearchParams(
          searchString.startsWith("?") ? searchString.slice(1) : searchString
        )
      : searchString instanceof URLSearchParams
      ? searchString
      : new URLSearchParams();

  const out = {};
  const o = parseCityKey(sp.get("o"));
  const d = parseCityKey(sp.get("d"));
  if (o && o.city) out.originKey = o;
  if (d && d.city) out.destinationKey = d;

  const dd = fromYMD(sp.get("dd"));
  const rd = fromYMD(sp.get("rd"));
  if (dd) out.departureDate = dd;
  if (rd) out.returnDate = rd;

  const a = sp.get("a");
  const c = sp.get("c");
  if (a != null && a !== "") out.adults = Math.max(1, parseInt(a, 10) || 1);
  if (c != null && c !== "") out.children = Math.max(0, parseInt(c, 10) || 0);

  const ft = sp.get("ft");
  if (ft === "roundtrip" || ft === "oneway") out.flightType = ft;

  const at = sp.get("at");
  if (at) out.airbnbType = at;

  const st = sp.get("st");
  if (st) out.searchType = st;

  const mrc = sp.get("mrc");
  if (mrc != null && mrc !== "") out.minReviewCount = parseInt(mrc, 10) || 0;

  const mrs = sp.get("mrs");
  if (mrs != null && mrs !== "") out.minReviewScore = mrs;

  const cur = sp.get("cur");
  if (cur) out.userCurrency = cur;

  const af = sp.get("af");
  if (af) {
    const decoded = b64decode(af);
    if (decoded && typeof decoded === "object") out.advancedFilters = decoded;
  }
  return out;
}

// Find a city object in citiesData by the parsed { city, admin, country_code } key.
// Falls back to city-only match if admin/country fail (older saved URLs).
export function findCityByKey(citiesData, key) {
  if (!key || !key.city || !Array.isArray(citiesData)) return null;
  const exact = citiesData.find(
    (c) =>
      c.city === key.city &&
      (c.admin || "") === (key.admin || "") &&
      (c.country_code || "") === (key.country_code || "")
  );
  if (exact) return exact;
  return (
    citiesData.find(
      (c) => c.city === key.city && (c.country_code || "") === (key.country_code || "")
    ) || null
  );
}

// Stable string key for a params object, used to compare two searches for
// equality (e.g. "is this current search already saved?"). Sorts keys so two
// equivalent param objects produce the same key regardless of insertion order.
export function canonicalKeyFromParams(params) {
  if (!params || typeof params !== "object") return "";
  return Object.keys(params)
    .sort()
    .map((k) => `${k}=${typeof params[k] === "string" ? params[k] : JSON.stringify(params[k])}`)
    .join("&");
}

// Convenience: derive canonicalKey directly from current search state.
export function canonicalKeyFromState(state) {
  return canonicalKeyFromParams(buildSearchParams(state));
}

// Human label for a search, used as the default name when saving.
export function describeSearch({ origin, destination, departureDate, returnDate }) {
  const o = origin?.city || "Anywhere";
  const d = destination?.city || "Anywhere";
  const fmt = (d) => {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const dd = fmt(departureDate);
  const rd = fmt(returnDate);
  const dates = rd ? `${dd} → ${rd}` : dd;
  return dates ? `${o} → ${d} · ${dates}` : `${o} → ${d}`;
}
